// CleanupContactEditPanel — editierbare Kontakt-Stammdaten für die Ansprechpartner-
// Bereinigung („Bisherige Informationen"). Wiederverwendet den bestehenden Hook
// `useContactGeneralForm` (lädt GET /contact/:id, hält Formular-State, speichert via
// PUT /contact/:id) und rendert die Stammdaten mit den Form-Primitives — OHNE
// Verbindungen/Verknüpfungen, OHNE Ownership/Social. Eigener Speichern-Button,
// getrennt vom Bereinigungs-Speichern. RBAC: nur bei `canWrite` editierbar.

import * as React from "react";
import { makeStyles, shorthands, Toaster, Spinner } from "@fluentui/react-components";
import { useContactGeneralForm } from "./redesign/useContactGeneralForm";
import { useBerufsstatusOptions, withBerufsstatusValue } from "../hooks/useBerufsstatusOptions";
import { SectionCard, FieldRow, LabeledInput, LabeledSelect } from "./forms/formPrimitives";
import type { ContactDetailsFormValues } from "../types/Contact";
import { BRAND } from "./risikoanalyseStep/tokens";
// Familienstand aus der gemeinsamen Quelle: eine zeichengleiche Kopie hier
// hätte irgendwann eine Option mehr oder weniger als Anlage und Akte.
import { FAMILIENSTAND_OPTIONS } from "../utils/familienstand";
import { PhoneInput } from "./forms/widgets/PhoneInput";

const REGISTER_OPTIONS = ["HRA", "HRB", "GnR", "GsR", "PR", "VR"];
const GESCHLECHT_OPTIONS = ["Männlich", "Weiblich", "Divers", "Ohne Angabe"];
// Berufsstatus: siehe hooks/useBerufsstatusOptions (Katalog aus dem Backend).
const JANEIN_OPTIONS = [
  { value: "Ja", label: "Ja" },
  { value: "Nein", label: "Nein" },
];
const strOpts = (arr: string[]) => arr.map((o) => ({ value: o, label: o }));

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", rowGap: "16px", width: "100%" },
  loading: { display: "inline-flex", alignItems: "center", columnGap: "8px", color: BRAND.muted, fontSize: "0.85rem" },
  notice: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", "#ECE7F0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
  },
  readOnly: { opacity: 0.7, pointerEvents: "none" },
  saveBar: { display: "flex", justifyContent: "flex-end", columnGap: "10px", alignItems: "center" },
  saveBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("9px", "18px"),
    backgroundColor: BRAND.purple,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.88rem",
    cursor: "pointer",
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
});

// Imperative Schnittstelle für die Abschließen-Orchestrierung (Altkunden, SP9):
// der übergeordnete „Speichern & abschließen"-Button kann das Stammdaten-Speichern
// zentral auslösen (statt eines eigenen, konkurrierenden Panel-Buttons).
export interface CleanupContactEditHandle {
  /** Speichert die Stammdaten, wenn dirty. true = erfolgreich (oder nichts zu tun). */
  save: () => Promise<boolean>;
  /** Ob ungespeicherte Stammdaten-Änderungen vorliegen. */
  isDirty: () => boolean;
  /** Ob der aktuelle Nutzer die Stammdaten schreiben darf. */
  canWrite: () => boolean;
}

interface CleanupContactEditPanelProps {
  contactId: number;
  /**
   * Blendet die eigene Speicher-Leiste des Panels aus. Genutzt von der
   * Abschließen-Orchestrierung (SP9), damit es nur EINEN Speicher-Pfad gibt
   * (kein konkurrierender Dirty-State / Doppel-Save). Default: false → das
   * Panel behält seinen eigenen Speichern-Button (AP-/Haushalts-Bereinigung).
   */
  hideSaveBar?: boolean;
}

export const CleanupContactEditPanel = React.forwardRef<
  CleanupContactEditHandle,
  CleanupContactEditPanelProps
>(function CleanupContactEditPanel({ contactId, hideSaveBar = false }, ref) {
  const s = useStyles();
  const {
    contactData,
    setContactData,
    loading,
    isDirty,
    canWrite,
    anredeOptions,
    titelOptions,
    legalFormOptions,
    gesellschaftsform,
    setGesellschaftsform,
    setOldLegalForm,
    setField,
    commValue,
    setComm,
    commValueFirma,
    setCommFirma,
    faxValue,
    setFax,
    handleSave,
    toasterId,
  } = useContactGeneralForm(contactId, true, { cleanupWrite: true });

  const [saving, setSaving] = React.useState(false);
  const doSave = async (): Promise<boolean> => {
    setSaving(true);
    try {
      return await handleSave();
    } finally {
      setSaving(false);
    }
  };

  // Imperative Handle für die Abschließen-Orchestrierung (SP9). save() speichert
  // nur, wenn dirty (sonst no-op → true), damit die Sequenz nicht an einem
  // unnötigen PUT scheitert. Refs lesen isDirty/canWrite zur Aufrufzeit.
  React.useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (!canWrite) return true; // nichts zu speichern, kein Fehler
        if (!isDirty) return true; // keine Änderungen → Schritt überspringen
        return doSave();
      },
      isDirty: () => isDirty,
      canWrite: () => canWrite,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, isDirty, handleSave],
  );

  // Berufsstatus aus dem gepflegten Katalog (ContactBerufsstatus). Muss VOR den
  // Early-Returns stehen — Hooks dürfen nicht bedingt laufen.
  const berufsstatusOptions = useBerufsstatusOptions();

  const cd = contactData;
  if (loading && !cd) {
    return (
      <span className={s.loading}>
        <Spinner size="tiny" /> Lade Kontaktdaten …
      </span>
    );
  }
  if (!cd) return <span className={s.notice}>Kontaktdaten konnten nicht geladen werden.</span>;

  // Default „Person", da der Ausklapp ausschließlich für Personen eingebunden wird;
  // bei einer Firma liefert die API typ/type.type='Firma' und schaltet korrekt um.
  const typ = (cd.typ ?? cd.type?.type ?? "Person") as string | number;
  const isCompany = typ === "Firma";

  // --- Feld-Bindungen (Werte als String; Setter über den Hook) ---
  const fval = (key: keyof ContactDetailsFormValues) => (cd[key] ?? "") as string;
  const fon = (key: keyof ContactDetailsFormValues) => (v: string) => setField(key, v);
  const dateVal = (key: keyof ContactDetailsFormValues) => ((cd[key] ?? "") as string).slice(0, 10);
  const janein = (key: keyof ContactDetailsFormValues): string => {
    const raw = cd[key];
    return raw == null ? "" : raw ? "Ja" : "Nein";
  };
  const onJanein = (key: keyof ContactDetailsFormValues) => (v: string) =>
    setField(key, v === "" ? null : v === "Ja");

  // Rechtsform setzt zugleich den Rechtsvorgänger (wie in der Detailansicht).
  const onLegalForm = (v: string) => {
    const selected = legalFormOptions.find((lf) => lf.legalForm === v);
    const prevLabel = gesellschaftsform;
    const prevSelected = legalFormOptions.find((lf) => lf.legalForm === prevLabel);
    setGesellschaftsform(v);
    setContactData((c) => {
      if (!c) return c;
      const nd: ContactDetailsFormValues = { ...c, legalForm: selected ?? null };
      if (prevSelected && prevLabel && prevLabel !== v) {
        setOldLegalForm(prevLabel);
        nd.oldLegalForm = prevSelected;
      }
      return nd;
    });
  };
  const onVorname = (v: string) =>
    setContactData((c) => (c ? { ...c, vorname: v, firstName: v } : c));

  const anredeOpts = anredeOptions.map((o) => ({ value: String(o.id), label: o.salutation ?? "" }));
  const titelOpts = titelOptions.map((o) => ({ value: String(o.id), label: o.title ?? "" }));
  const legalFormOpts = legalFormOptions.map((lf) => ({ value: lf.legalForm, label: lf.legalForm }));

  const fields = isCompany ? (
    <>
      <SectionCard title="Allgemein">
        <FieldRow>
          <LabeledInput label="Firmenname" name={`c${contactId}-lastname`} value={fval("lastname")} onChange={fon("lastname")} />
          <LabeledSelect label="Rechtsform" name={`c${contactId}-legalForm`} value={gesellschaftsform} onChange={onLegalForm} options={legalFormOpts} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Gründungsdatum" name={`c${contactId}-birthDate`} type="date" value={dateVal("birthDate")} onChange={fon("birthDate")} />
          <LabeledInput label="Geschäftsjahr (Bilanzstichtag)" name={`c${contactId}-fye`} value={fval("financialYearEnd")} onChange={fon("financialYearEnd")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Branche" name={`c${contactId}-branche`} value={fval("branche")} onChange={fon("branche")} />
          <LabeledInput label="Tätigkeit" name={`c${contactId}-taetigkeit`} value={fval("taetigkeit")} onChange={fon("taetigkeit")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Mitgliedschaften" name={`c${contactId}-memberships`} value={fval("mitgliedschaften")} onChange={fon("mitgliedschaften")} />
          <LabeledSelect label="Vorsteuerabzugsberechtigung" name={`c${contactId}-vsab`} value={janein("vorsteuerAbzugsberechtigt")} onChange={onJanein("vorsteuerAbzugsberechtigt")} options={JANEIN_OPTIONS} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Steuer- & Rechtsdaten">
        <FieldRow>
          <LabeledInput label="Steuernummer" name={`c${contactId}-steuernummer`} value={fval("steuernummer")} onChange={fon("steuernummer")} />
          <LabeledInput label="Umsatzsteuer-IdNr." name={`c${contactId}-ustid`} value={fval("umsatzsteuernummer")} onChange={fon("umsatzsteuernummer")} />
        </FieldRow>
        <FieldRow>
          <LabeledSelect label="Handelsregister" name={`c${contactId}-register`} value={fval("register")} onChange={fon("register")} options={strOpts(REGISTER_OPTIONS)} />
          <LabeledInput label="Handelsregisternummer" name={`c${contactId}-hrn`} value={fval("handelsregisterNummer")} onChange={fon("handelsregisterNummer")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Zuständiges Finanzamt" name={`c${contactId}-finanzamt`} value={fval("finanzamt")} onChange={fon("finanzamt")} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Anschrift">
        <FieldRow>
          <LabeledInput label="Straße und Hausnummer" name={`c${contactId}-strasse`} value={fval("strasse")} onChange={fon("strasse")} />
          <LabeledInput label="Ortsteil" name={`c${contactId}-ortsteil`} value={fval("ortsteil")} onChange={fon("ortsteil")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="PLZ" name={`c${contactId}-plz`} value={fval("plz")} onChange={fon("plz")} />
          <LabeledInput label="Ort" name={`c${contactId}-ort`} value={fval("ort")} onChange={fon("ort")} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Kommunikation">
        <FieldRow>
          <PhoneInput actionButton={false} kind="landline" geschaeftlich label="Telefon" name={`c${contactId}-fphone`} value={commValueFirma("phone")} onChange={(v) => setCommFirma("phone", v)} />
          <PhoneInput actionButton={false} kind="mobile" geschaeftlich label="Mobil" name={`c${contactId}-fmobile`} value={commValueFirma("mobile")} onChange={(v) => setCommFirma("mobile", v)} />
        </FieldRow>
        <FieldRow>
          <PhoneInput actionButton={false} kind="fax" geschaeftlich zentrale={commValueFirma("phone")} label="Telefax" name={`c${contactId}-ffax`} value={commValueFirma("fax")} onChange={(v) => setCommFirma("fax", v)} />
          <LabeledInput label="E-Mail" name={`c${contactId}-femail`} type="email" value={commValueFirma("email")} onChange={(v) => setCommFirma("email", v)} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Bankverbindung">
        <FieldRow>
          <LabeledInput label="Bank" name={`c${contactId}-bank`} value={fval("kreditinstitut")} onChange={fon("kreditinstitut")} />
          <LabeledInput label="Kontoinhaber" name={`c${contactId}-holder`} value={fval("accountHolder")} onChange={fon("accountHolder")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="IBAN" name={`c${contactId}-iban`} value={fval("iban")} onChange={fon("iban")} />
          <LabeledInput label="BIC" name={`c${contactId}-bic`} value={fval("bic")} onChange={fon("bic")} />
        </FieldRow>
      </SectionCard>
    </>
  ) : (
    <>
      <SectionCard title="Allgemein">
        <FieldRow>
          <LabeledSelect label="Anrede" name={`c${contactId}-anrede`} value={fval("anrede")} onChange={fon("anrede")} options={anredeOpts} />
          <LabeledSelect label="Titel" name={`c${contactId}-titel`} value={fval("titel")} onChange={fon("titel")} options={titelOpts} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Nachname" name={`c${contactId}-lastname`} value={fval("lastname")} onChange={fon("lastname")} />
          <LabeledInput label="Vorname (gemäß Personalausweis)" name={`c${contactId}-vorname`} value={fval("vorname")} onChange={onVorname} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Rufname" name={`c${contactId}-rufname`} value={fval("rufname")} onChange={fon("rufname")} />
          <LabeledSelect label="Geschlecht" name={`c${contactId}-gender`} value={fval("geschlecht")} onChange={fon("geschlecht")} options={strOpts(GESCHLECHT_OPTIONS)} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Geburtsdatum" name={`c${contactId}-birthDate`} type="date" value={dateVal("birthDate")} onChange={fon("birthDate")} />
          <LabeledInput label="Geburtsname" name={`c${contactId}-geburtsname`} value={fval("geburtsname")} onChange={fon("geburtsname")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Geburtsort" name={`c${contactId}-geburtsort`} value={fval("geburtsort")} onChange={fon("geburtsort")} />
          <LabeledInput label="Staatsangehörigkeit" name={`c${contactId}-staatsang`} value={fval("staatsang")} onChange={fon("staatsang")} />
        </FieldRow>
        <FieldRow>
          <LabeledSelect label="Familienstand" name={`c${contactId}-familienstand`} value={fval("familienstand")} onChange={fon("familienstand")} options={strOpts(FAMILIENSTAND_OPTIONS)} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Anschrift">
        <FieldRow>
          <LabeledInput label="Straße und Hausnummer" name={`c${contactId}-strasse`} value={fval("strasse")} onChange={fon("strasse")} />
          <LabeledInput label="Ortsteil" name={`c${contactId}-ortsteil`} value={fval("ortsteil")} onChange={fon("ortsteil")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="PLZ" name={`c${contactId}-plz`} value={fval("plz")} onChange={fon("plz")} />
          <LabeledInput label="Ort" name={`c${contactId}-ort`} value={fval("ort")} onChange={fon("ort")} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Kommunikation">
        <FieldRow>
          <PhoneInput actionButton={false} kind="landline" label="Telefon privat" name={`c${contactId}-pphone`} value={commValue("phone", "privat")} onChange={(v) => setComm("phone", "privat", v)} />
          <PhoneInput actionButton={false} kind="landline" geschaeftlich label="Telefon geschäftlich" name={`c${contactId}-gphone`} value={commValue("phone", "geschäftlich")} onChange={(v) => setComm("phone", "geschäftlich", v)} />
        </FieldRow>
        <FieldRow>
          <PhoneInput actionButton={false} kind="mobile" label="Mobil privat" name={`c${contactId}-pmobile`} value={commValue("mobile", "privat")} onChange={(v) => setComm("mobile", "privat", v)} />
          <PhoneInput actionButton={false} kind="mobile" geschaeftlich label="Mobil geschäftlich" name={`c${contactId}-gmobile`} value={commValue("mobile", "geschäftlich")} onChange={(v) => setComm("mobile", "geschäftlich", v)} />
        </FieldRow>
        <FieldRow>
          <PhoneInput actionButton={false} kind="fax" label="Telefax" name={`c${contactId}-fax`} value={faxValue} onChange={setFax} />
          <LabeledInput label="E-Mail privat" name={`c${contactId}-pemail`} type="email" value={commValue("email", "privat")} onChange={(v) => setComm("email", "privat", v)} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="E-Mail geschäftlich" name={`c${contactId}-gemail`} type="email" value={commValue("email", "geschäftlich")} onChange={(v) => setComm("email", "geschäftlich", v)} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Beruf">
        <FieldRow>
          <LabeledSelect label="Status" name={`c${contactId}-berufsstatus`} value={fval("berufsstatus")} onChange={fon("berufsstatus")} options={strOpts(withBerufsstatusValue(berufsstatusOptions, fval("berufsstatus")))} />
          <LabeledInput label="Arbeitgeber" name={`c${contactId}-arbeitgeber`} value={fval("arbeitgeber")} onChange={fon("arbeitgeber")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="Branche" name={`c${contactId}-pbranche`} value={fval("branche")} onChange={fon("branche")} />
          <LabeledInput label="Tätigkeit" name={`c${contactId}-ptaetigkeit`} value={fval("taetigkeit")} onChange={fon("taetigkeit")} />
        </FieldRow>
      </SectionCard>

      <SectionCard title="Bankverbindung">
        <FieldRow>
          <LabeledInput label="Bank" name={`c${contactId}-bank`} value={fval("kreditinstitut")} onChange={fon("kreditinstitut")} />
          <LabeledInput label="Kontoinhaber" name={`c${contactId}-holder`} value={fval("accountHolder")} onChange={fon("accountHolder")} />
        </FieldRow>
        <FieldRow>
          <LabeledInput label="IBAN" name={`c${contactId}-iban`} value={fval("iban")} onChange={fon("iban")} />
          <LabeledInput label="BIC" name={`c${contactId}-bic`} value={fval("bic")} onChange={fon("bic")} />
        </FieldRow>
      </SectionCard>
    </>
  );

  return (
    <div className={s.wrap}>
      <Toaster toasterId={toasterId} />
      {!canWrite && (
        <span className={s.notice}>
          Keine Schreibberechtigung für diesen Kontakt — die Felder sind schreibgeschützt.
        </span>
      )}
      <div className={!canWrite ? s.readOnly : undefined}>{fields}</div>
      {canWrite && !hideSaveBar && (
        <div className={s.saveBar}>
          <button type="button" className={s.saveBtn} onClick={doSave} disabled={saving || !isDirty}>
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
});

export default CleanupContactEditPanel;
